import * as React from 'react';

import type { FieldVariant } from '@/lib/field-chrome';

const FieldVariantContext = React.createContext<FieldVariant>('solid');

/**
 * Wrap a subtree to make all form-field primitives default to a given visual
 * variant. Individual primitives can still override with an explicit
 * `variant` prop. Useful for auth/onboarding pages that should render fields
 * in the translucent `glass` style without threading the prop through every
 * field:
 *
 * ```tsx
 * <FieldVariantProvider variant="glass">
 *   <LoginForm />
 * </FieldVariantProvider>
 * ```
 */
export function FieldVariantProvider({
  variant,
  children,
}: {
  variant: FieldVariant;
  children: React.ReactNode;
}) {
  return <FieldVariantContext.Provider value={variant}>{children}</FieldVariantContext.Provider>;
}

/**
 * Resolve the effective field variant: the explicit `override` wins, then
 * the nearest `FieldVariantProvider`, and finally the default `'solid'`.
 */
export function useFieldVariant(override?: FieldVariant): FieldVariant {
  const ctx = React.useContext(FieldVariantContext);
  return override ?? ctx;
}
